if (!customElements.get('wlsn-counter')) {
  class WLSNCounter extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        time: 'js-wlsn-counter-time',
      };

      this.elements = {
        time: this.querySelector(`.${this.classes.time}`),
        days: this.querySelector('.js-wlsn-counter-days'),
        hours: this.querySelector('.js-wlsn-counter-hours'),
        minutes: this.querySelector('.js-wlsn-counter-minutes'),
        seconds: this.querySelector('.js-wlsn-counter-seconds'),
        daysSegment: this.querySelector('.js-wlsn-counter-days-segment'),
        daysSeparator: this.querySelector('.js-wlsn-counter-days-separator'),
      };

      this.timeZone = 'America/New_York';
      this.durationHours = Number(this.dataset.durationHours);

      if (Number.isFinite(this.durationHours) && this.durationHours > 0) {
        // Add just under one second so the first paint reads exactly 24:00:00.
        this.durationEndTimestamp = Date.now() + this.durationHours * 60 * 60 * 1000 + 999;
      }

      // Parse the target date. Keep null on an invalid/missing value so the
      // counter falls back to 00:00:00 instead of throwing.
      const rawDateTime = this.dataset.date; // Format: '12-05-2024:00:00'
      const dateTimeRegex = /^(\d{2})-(\d{2})-(\d{4}):(\d{2}):(\d{2})$/;
      const dateTimeParts = rawDateTime ? rawDateTime.match(dateTimeRegex) : null;

      if (!dateTimeParts && !this.durationEndTimestamp) {
        console.error("Invalid date format. Expected: mm-dd-yyyy:hh:mm. Got:", rawDateTime);
        this.endDate = null;
      } else if (this.durationEndTimestamp) {
        this.endDate = null;
      } else {
        const [_, month, day, year, hours, minutes] = dateTimeParts.map(Number);
        const formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
        this.endDate = new Date(formattedDate);
      }
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      this.timer = setInterval(() => this._initCounter(), 1000);
      this._initCounter();
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      clearInterval(this.timer);
    }

    /**
     * Get current time in New York timezone
     * @returns {Date} - Current date/time in NY timezone
     */
    _getCurrentTimeInNY() {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: this.timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });

      const parts = formatter.formatToParts(now);
      const [month, day, year, hours, minutes, seconds] = [
        parts.find((p) => p.type === 'month').value,
        parts.find((p) => p.type === 'day').value,
        parts.find((p) => p.type === 'year').value,
        parts.find((p) => p.type === 'hour').value,
        parts.find((p) => p.type === 'minute').value,
        parts.find((p) => p.type === 'second').value,
      ];

      return new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}`);
    }

    /**
     * Toggle the visibility of the days segment and its separator.
     * @param {boolean} visible - Whether the days segment should be shown.
     * @returns {void}
     */
    _toggleDays(visible) {
      const display = visible ? '' : 'none';
      if (this.elements.daysSegment) this.elements.daysSegment.style.display = display;
      if (this.elements.daysSeparator) this.elements.daysSeparator.style.display = display;
    }

    /**
     * Initialize and update counter display
     * @returns {void}
     */
    _initCounter() {
      if (!this.elements.time) return;
      this.elements.time.classList.add(this.classes.active);

      // Nothing to count down to (missing or invalid date). Leave as is.
      if (!this.durationEndTimestamp && (!this.endDate || isNaN(this.endDate.getTime()))) {
        clearInterval(this.timer);
        return;
      }

      const isDurationCounter = Boolean(this.durationEndTimestamp);
      const now = isDurationCounter ? Date.now() : this._getCurrentTimeInNY();
      let timeRemaining = isDurationCounter ? this.durationEndTimestamp - now : this.endDate - now;

      const pad = (num) => String(num).padStart(2, '0');

      // Time is up (target date already passed).
      if (timeRemaining <= 0) {
        clearInterval(this.timer);
        this.elements.time.innerText = "Time's up!";
        return;
      }

      const units = {
        days: 1000 * 60 * 60 * 24,
        hours: 1000 * 60 * 60,
        minutes: 1000 * 60,
        seconds: 1000,
      };

      let data;
      if (isDurationCounter) {
        data = {
          days: 0,
          hours: Math.floor(timeRemaining / units.hours),
          minutes: Math.floor((timeRemaining % units.hours) / units.minutes),
          seconds: Math.floor((timeRemaining % units.minutes) / units.seconds),
        };
        this._toggleDays(false);
      } else {
        data = Object.keys(units).reduce((acc, unit) => {
          acc[unit] = Math.floor(timeRemaining / units[unit]);
          timeRemaining %= units[unit];
          return acc;
        }, {});
        this._toggleDays(data.days > 0);
      }

      Object.keys(units).forEach((unit) => {
        if (this.elements[unit]) this.elements[unit].textContent = pad(data[unit]);
      });
    }
  }

  customElements.define('wlsn-counter', WLSNCounter);
}
